/**
 * "Ask for ideas": a small conversation for finding colours.
 *
 * The artist describes what they want ("warmer", "like a sunset", "match my logo") and/or attaches
 * an image, and gets three palettes back to try on their page. Each request carries the palette on
 * screen, so relative asks ("darker", "less pink") refine what they are looking at.
 */
import { useEffect, useRef, useState } from 'react'
import { ImagePlus, Loader2, Send, Sparkles, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { useSuggestPalettes } from '../../hooks'
import type { PaletteRoles, PaletteSuggestion, SuggestionQuota } from '../../types'
import { PaletteCard } from './PaletteCard'
import { samePalette } from './palettes'

const QUICK_ASKS = ['Warmer', 'Darker and moodier', 'More minimal', 'Brighter and bolder']
const MAX_IMAGE_BYTES = 10 * 1024 * 1024

interface Turn {
  id: number
  prompt: string
  imageUrl?: string
  status: 'pending' | 'done' | 'error'
  suggestions?: PaletteSuggestion[]
  note?: string
  error?: string
}

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string }
  return e?.response?.data?.message || e?.response?.data?.error || 'Something went wrong. Please try again.'
}

export function PaletteAssistant({
  current,
  selected,
  onTry,
  quota: initialQuota,
}: {
  /** The palette on screen, sent so relative requests refine it. */
  current: PaletteRoles
  selected: PaletteRoles | null
  onTry: (suggestion: PaletteSuggestion) => void
  /** Today's allowance, when known; each answered request updates it. */
  quota?: SuggestionQuota | null
}) {
  const suggest = useSuggestPalettes()
  const [quota, setQuota] = useState<SuggestionQuota | null>(initialQuota ?? null)
  useEffect(() => {
    if (initialQuota) setQuota(initialQuota)
  }, [initialQuota])
  const exhausted = quota !== null && quota.remaining <= 0
  const [prompt, setPrompt] = useState('')
  const [image, setImage] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [turns, setTurns] = useState<Turn[]>([])
  const [inputError, setInputError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const nextId = useRef(1)

  // Object URLs for attached images are released when the component goes away.
  const urls = useRef<string[]>([])
  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL(u)), [])

  const attach = (file: File | undefined) => {
    setInputError(null)
    if (!file) return
    if (!file.type.startsWith('image/')) return setInputError('Attach an image (JPG, PNG or WebP).')
    if (file.size > MAX_IMAGE_BYTES) return setInputError('That image is over 10MB.')
    const url = URL.createObjectURL(file)
    urls.current.push(url)
    setImage(file)
    setImageUrl(url)
  }

  const clearImage = () => {
    setImage(null)
    setImageUrl(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  const send = async (text: string) => {
    const ask = text.trim()
    if (!ask && !image) return setInputError('Describe what you want, or attach an image.')
    const id = nextId.current++
    setTurns((t) => [...t, { id, prompt: ask, imageUrl: imageUrl || undefined, status: 'pending' }])
    setPrompt('')
    const sentImage = image
    clearImage()
    setInputError(null)
    try {
      const result = await suggest.mutateAsync({ prompt: ask, current, image: sentImage })
      if (result.quota) setQuota(result.quota)
      setTurns((t) =>
        t.map((turn) =>
          turn.id === id
            ? {
                ...turn,
                status: 'done',
                suggestions: result.suggestions,
                note:
                  result.source === 'image'
                    ? 'Built from the colours in your image.'
                    : undefined,
              }
            : turn,
        ),
      )
    } catch (err) {
      const limit = (err as { response?: { data?: { errors?: { quota?: SuggestionQuota } } } })?.response?.data?.errors?.quota
      if (limit) setQuota(limit)
      setTurns((t) => t.map((turn) => (turn.id === id ? { ...turn, status: 'error', error: errorMessage(err) } : turn)))
    }
  }

  return (
    <div className="space-y-4">
      {turns.length > 0 && (
        <div className="space-y-4">
          {turns.map((turn) => (
            <div key={turn.id} className="space-y-3">
              <div className="flex justify-end">
                <div className="max-w-[85%] space-y-2 rounded-2xl rounded-br-sm bg-muted px-3 py-2 text-sm">
                  {turn.imageUrl && (
                    <img src={turn.imageUrl} alt="Attached" className="max-h-32 rounded-md object-cover" />
                  )}
                  {turn.prompt && <p>{turn.prompt}</p>}
                </div>
              </div>
              {turn.status === 'pending' && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Mixing some colours…
                </p>
              )}
              {turn.status === 'error' && <p className="text-sm text-destructive">{turn.error}</p>}
              {turn.status === 'done' && turn.suggestions && (
                <div className="space-y-2">
                  <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Sparkles className="h-3.5 w-3.5" />
                    {turn.note || 'Here are a few ideas. Tap one to see it on your page.'}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {turn.suggestions.map((s, i) => (
                      <PaletteCard
                        key={i}
                        name={s.name}
                        description={s.description}
                        colors={s.colors}
                        selected={samePalette(selected, s.colors)}
                        onSelect={() => onTry(s)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border bg-background p-2 focus-within:ring-1 focus-within:ring-ring">
        {imageUrl && (
          <div className="relative mb-2 inline-block">
            <img src={imageUrl} alt="To match" className="h-16 w-16 rounded-md object-cover" />
            <button
              type="button"
              onClick={clearImage}
              aria-label="Remove image"
              className="absolute -right-2 -top-2 rounded-full border bg-background p-0.5 shadow"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <Textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void send(prompt)
            }
          }}
          rows={2}
          maxLength={500}
          placeholder="Describe the feel you want, e.g. “earthy and calm, like my studio”, or attach an image to match"
          className="min-h-0 resize-none border-0 p-1 shadow-none focus-visible:ring-0"
        />
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              aria-label="Attach an image"
              onChange={(e) => attach(e.target.files?.[0])}
            />
            <Button type="button" variant="ghost" size="sm" className="h-8" onClick={() => fileRef.current?.click()}>
              <ImagePlus className="mr-1.5 h-4 w-4" />
              Image
            </Button>
            {QUICK_ASKS.map((q) => (
              <Button
                key={q}
                type="button"
                variant="outline"
                size="sm"
                className="h-7 rounded-full px-2.5 text-xs"
                disabled={suggest.isPending || exhausted}
                onClick={() => void send(q)}
              >
                {q}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            size="icon"
            className="h-8 w-8 shrink-0"
            aria-label="Send"
            disabled={suggest.isPending || exhausted || (!prompt.trim() && !image)}
            onClick={() => void send(prompt)}
          >
            {suggest.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      {inputError && <p className="text-sm text-destructive">{inputError}</p>}
      {quota && (
        <p className="text-xs text-muted-foreground">
          {exhausted
            ? `You've used today's ${quota.limit} ideas. More tomorrow; the palettes above and Fine-tune still work.`
            : `${quota.remaining} of ${quota.limit} ideas left today.`}
        </p>
      )}
    </div>
  )
}
